import type { PolicyConfig } from '@pf/contracts';
import {
  ArrowLeftRight,
  BadgeCheck,
  Bot,
  FlaskConical,
  KeyRound,
  Landmark,
  RotateCcw,
  Save,
  Scale,
  Scissors,
  ShieldCheck,
  Timer,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/auth-context';
import type { MockScenario } from '@pf/contracts';
import {
  useChangePassword,
  useMockScenario,
  usePolicy,
  useSetMockScenario,
  useUpdatePolicy,
} from '@/lib/api/ops-queries';
import { formatDuration, formatRelative } from '@/lib/datetime';
import { seconds } from '@/lib/clock';
import { cn } from '@/lib/utils';
import { PageHeader, Section, Field } from '@/components/layout/page';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Callout, ErrorState, Skeleton } from '@/components/ui/feedback';
import {
  Code,
  Input,
  Label,
  Pill,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/primitives';

/**
 * Settings — policy tunables, the mock scenario switch, and profile/2FA.
 *
 * The policy tab is the SAD §16 Q1–Q8 open questions made editable, and every field says which
 * question it answers. That mapping is the point: the SAD deliberately leaves these values to the
 * business, and a console that buried them in a config file would make "business-tunable" a
 * fiction. Grep the domain for a numeric literal and you should find only 0 and 1 — every number
 * below is the reason why.
 *
 * The scenario switch drives `apps/mock-slipok`. It is here rather than in a test fixture so a
 * reviewer can walk the whole SAD §6 decision matrix — auto-approve, duplicate, re-submit,
 * amount mismatch, provider down — through the real UI in about a minute.
 */
export function SettingsPage(): ReactNode {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader title={t('settings.title')} description={t('settings.description')} />

      <Tabs defaultValue="policy">
        <TabsList className="w-full justify-start overflow-x-auto sm:w-auto">
          <TabsTrigger value="policy">{t('settings.tabs.policy')}</TabsTrigger>
          <TabsTrigger value="scenario">{t('settings.tabs.scenario')}</TabsTrigger>
          <TabsTrigger value="profile">{t('settings.tabs.profile')}</TabsTrigger>
        </TabsList>

        <TabsContent value="policy">
          <PolicyTab />
        </TabsContent>
        <TabsContent value="scenario">
          <ScenarioTab />
        </TabsContent>
        <TabsContent value="profile">
          <ProfileTab />
        </TabsContent>
      </Tabs>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------------------------

interface PolicyField {
  key: keyof PolicyConfig;
  question: string;
  label: string;
  help: string;
  kind: 'seconds' | 'ratio' | 'count' | 'money' | 'banks' | 'toggle' | 'percent-nullable';
  /** Example input, for the `banks` kind where the codes to type are not guessable. */
  placeholder?: string;
}

function buildPolicyGroups(
  t: (key: string) => string,
): { title: string; icon: typeof Timer; description: string; fields: PolicyField[] }[] {
  return [
    {
      title: t('settings.policy.groups.timeouts.title'),
      icon: Timer,
      description: t('settings.policy.groups.timeouts.description'),
      fields: [
        {
          key: 'reservation_ttl_seconds',
          question: 'Q1',
          label: t('settings.policy.fields.reservationTtl.label'),
          help: t('settings.policy.fields.reservationTtl.help'),
          kind: 'seconds',
        },
        {
          key: 'evidence_deadline_seconds',
          question: 'Q1',
          label: t('settings.policy.fields.evidenceDeadline.label'),
          help: t('settings.policy.fields.evidenceDeadline.help'),
          kind: 'seconds',
        },
        {
          key: 'verification_max_seconds',
          question: 'Q1',
          label: t('settings.policy.fields.verificationMax.label'),
          help: t('settings.policy.fields.verificationMax.help'),
          kind: 'seconds',
        },
        {
          key: 'review_sla_seconds',
          question: 'Q1',
          label: t('settings.policy.fields.reviewSla.label'),
          help: t('settings.policy.fields.reviewSla.help'),
          kind: 'seconds',
        },
        {
          key: 'late_slip_grace_seconds',
          question: 'Q1',
          label: t('settings.policy.fields.lateSlipGrace.label'),
          help: t('settings.policy.fields.lateSlipGrace.help'),
          kind: 'seconds',
        },
        {
          key: 'withdrawal_max_age_seconds',
          question: 'Q1',
          label: t('settings.policy.fields.withdrawalMaxAge.label'),
          help: t('settings.policy.fields.withdrawalMaxAge.help'),
          kind: 'seconds',
        },
      ],
    },
    {
      title: t('settings.policy.groups.nameMatching.title'),
      icon: Scale,
      description: t('settings.policy.groups.nameMatching.description'),
      fields: [
        {
          key: 'name_match_auto_approve_threshold',
          question: 'Q2',
          label: t('settings.policy.fields.autoApproveThreshold.label'),
          help: t('settings.policy.fields.autoApproveThreshold.help'),
          kind: 'ratio',
        },
        {
          key: 'name_match_cross_script_floor',
          question: '§6',
          label: t('settings.policy.fields.crossScriptFloor.label'),
          help: t('settings.policy.fields.crossScriptFloor.help'),
          kind: 'ratio',
        },
        {
          key: 'name_match_resubmit_floor',
          question: 'Q2',
          label: t('settings.policy.fields.resubmitFloor.label'),
          help: t('settings.policy.fields.resubmitFloor.help'),
          kind: 'ratio',
        },
      ],
    },
    {
      title: t('settings.policy.groups.bankGates.title'),
      icon: Landmark,
      description: t('settings.policy.groups.bankGates.description'),
      fields: [
        {
          key: 'same_bank_match_blocked_banks',
          question: '§5',
          label: t('settings.policy.fields.sameBankBlockedBanks.label'),
          help: t('settings.policy.fields.sameBankBlockedBanks.help'),
          kind: 'banks',
          placeholder: 'BBL, GSB',
        },
        {
          key: 'blocked_payer_banks',
          question: '§5',
          label: t('settings.policy.fields.blockedPayerBanks.label'),
          help: t('settings.policy.fields.blockedPayerBanks.help'),
          kind: 'banks',
          placeholder: 'KKP',
        },
      ],
    },
    {
      title: t('settings.policy.groups.amountReconcile.title'),
      icon: ArrowLeftRight,
      description: t('settings.policy.groups.amountReconcile.description'),
      fields: [
        {
          key: 'amount_auto_reconcile_under',
          question: '§6',
          label: t('settings.policy.fields.amountReconcileUnder.label'),
          help: t('settings.policy.fields.amountReconcileUnder.help'),
          kind: 'toggle',
        },
        {
          key: 'amount_auto_reconcile_over',
          question: '§6',
          label: t('settings.policy.fields.amountReconcileOver.label'),
          help: t('settings.policy.fields.amountReconcileOver.help'),
          kind: 'toggle',
        },
        {
          key: 'amount_auto_reconcile_cross_web',
          question: '§15',
          label: t('settings.policy.fields.amountReconcileCrossWeb.label'),
          help: t('settings.policy.fields.amountReconcileCrossWeb.help'),
          kind: 'toggle',
        },
      ],
    },
    {
      title: t('settings.policy.groups.splitFill.title'),
      icon: Scissors,
      description: t('settings.policy.groups.splitFill.description'),
      fields: [
        {
          key: 'split_min_slice_percent',
          question: '§5',
          label: t('settings.policy.fields.splitMinSlicePercent.label'),
          help: t('settings.policy.fields.splitMinSlicePercent.help'),
          kind: 'percent-nullable',
        },
        {
          key: 'split_fill_cross_web',
          question: '§15',
          label: t('settings.policy.fields.splitFillCrossWeb.label'),
          help: t('settings.policy.fields.splitFillCrossWeb.help'),
          kind: 'toggle',
        },
      ],
    },
    {
      title: t('settings.policy.groups.autoApprove.title'),
      icon: Bot,
      description: t('settings.policy.groups.autoApprove.description'),
      fields: [
        {
          key: 'ai_auto_approve_sender_mismatch',
          question: '§6',
          label: t('settings.policy.fields.aiAutoApproveSenderMismatch.label'),
          help: t('settings.policy.fields.aiAutoApproveSenderMismatch.help'),
          kind: 'toggle',
        },
        {
          key: 'ai_auto_approve_holdout_percent',
          question: '§6',
          label: t('settings.policy.fields.aiAutoApproveHoldoutPercent.label'),
          help: t('settings.policy.fields.aiAutoApproveHoldoutPercent.help'),
          kind: 'count',
        },
      ],
    },
    {
      title: t('settings.policy.groups.abuseControls.title'),
      icon: ShieldCheck,
      description: t('settings.policy.groups.abuseControls.description'),
      fields: [
        {
          key: 'max_evidence_resubmit_attempts',
          question: 'Q8',
          label: t('settings.policy.fields.maxSlipAttempts.label'),
          help: t('settings.policy.fields.maxSlipAttempts.help'),
          kind: 'count',
        },
        {
          key: 'duplicate_trans_ref_auto_reject',
          question: '§6',
          label: t('settings.policy.fields.duplicateTransRefAutoReject.label'),
          help: t('settings.policy.fields.duplicateTransRefAutoReject.help'),
          kind: 'toggle',
        },
        {
          key: 'max_concurrent_reservations_per_depositor',
          question: 'Q4',
          label: t('settings.policy.fields.concurrentReservations.label'),
          help: t('settings.policy.fields.concurrentReservations.help'),
          kind: 'count',
        },
        {
          key: 'dual_control_threshold',
          question: 'Q7',
          label: t('settings.policy.fields.dualControlThreshold.label'),
          help: t('settings.policy.fields.dualControlThreshold.help'),
          kind: 'money',
        },
      ],
    },
  ];
}

/**
 * The draft form holds every field as a string, but `split_min_slice_percent` is the one
 * nullable value on the wire (`null` = rule off) — `String(null)` would freeze that state as the
 * literal text "null" instead of the empty, "not set" input the off-toggle expects. This is the
 * one conversion every draft read/write goes through so the off state round-trips.
 */
const toDraftString = (value: PolicyConfig[keyof PolicyConfig] | undefined): string =>
  value === null ? '' : String(value);

function PolicyTab(): ReactNode {
  const { t } = useTranslation();
  const policy = usePolicy();
  const update = useUpdatePolicy();
  const { can } = useAuth();
  const [draft, setDraft] = useState<Record<string, string>>({});
  // `split_min_slice_percent` needs an on/off state that survives the number field going
  // momentarily blank while an operator retypes it — tying "disabled" to "draft string is
  // empty" would lock the input the instant they clear it to enter a new value. So the toggle
  // gets its own bit, independent of the draft string it gates.
  const [splitFillEnabled, setSplitFillEnabled] = useState(false);
  /**
   * Which fields the operator has actually edited since the last sync with the server.
   *
   * The effect below re-runs on EVERY refetch — and react-query refetches on window focus, so
   * "flip a toggle, alt-tab to a terminal, come back, press save" used to wipe the flip before
   * the save could see it (2026-08-28: the ตั้งค่า save that never sent a request — the button
   * was disabled because the wiped form was no longer dirty, and nothing said so). A wholesale
   * re-init is only safe for fields the operator has not touched; touched ones keep their draft
   * until the server catches up (their edit landed, or was discarded), and only then unpin.
   */
  const touched = useRef(new Set<string>());
  const splitFillTouched = useRef(false);
  const policyGroups = buildPolicyGroups(t);

  useEffect(() => {
    if (!policy.data) return;
    const server = Object.fromEntries(
      Object.entries(policy.data).map(([key, value]) => [key, toDraftString(value)]),
    );
    setDraft((prev) => {
      const next = { ...server };
      for (const key of [...touched.current]) {
        if (prev[key] !== undefined && prev[key] !== server[key]) {
          next[key] = prev[key];
        } else {
          // The server now agrees with the edit (the save landed) — it is no longer an edit.
          touched.current.delete(key);
        }
      }
      return next;
    });
    // `!= null` on purpose: an API predating this knob omits the field entirely, and absent
    // must read as "rule off" — `undefined !== null` would render a dead On-toggle whose empty
    // input fails validation and disables Save for every tunable on the page.
    const serverSplitOn = policy.data.split_min_slice_percent != null;
    if (splitFillTouched.current) {
      setSplitFillEnabled((prevOn) => {
        if (prevOn === serverSplitOn) splitFillTouched.current = false;
        return prevOn;
      });
    } else {
      setSplitFillEnabled(serverSplitOn);
    }
  }, [policy.data]);

  if (policy.isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-56" />
        ))}
      </div>
    );
  }
  if (policy.isError) {
    return (
      <ErrorState
        error={policy.error}
        onRetry={() => {
          void policy.refetch();
        }}
      />
    );
  }
  if (!policy.data) return null;

  const current = policy.data;
  // Absent on the wire (an API predating this knob) is the same as null: rule off.
  const splitMinSliceCurrent = current.split_min_slice_percent ?? null;

  // The one field where "off" is a value rather than an omission, so it gets its own client
  // check mirroring the wire schema (`z.number().int().min(1).max(100).nullable()`) instead of
  // trusting `Number(raw)` the way the plain numeric kinds do.
  const splitMinSlicePercentRaw = draft['split_min_slice_percent'] ?? '';
  const splitMinSlicePercentInvalid =
    splitFillEnabled &&
    (!/^\d+$/.test(splitMinSlicePercentRaw) ||
      Number(splitMinSlicePercentRaw) < 1 ||
      Number(splitMinSlicePercentRaw) > 100);
  // What the form would save right now: the number while the rule is on, null while off. Dirty
  // and the patch are both judged on this, never on the raw draft string — toggling On and back
  // Off must not count as a change, and must never send an empty patch that reports "saved" for
  // a write that did not happen.
  const splitMinSliceEffective = splitFillEnabled ? Number(splitMinSlicePercentRaw) : null;

  const dirty =
    Object.entries(draft).some(
      ([key, value]) =>
        key !== 'split_min_slice_percent' &&
        toDraftString(current[key as keyof PolicyConfig]) !== value,
    ) || splitMinSliceEffective !== splitMinSliceCurrent;

  const save = (): void => {
    // Built through a mutable record and narrowed once: `PolicyConfig`'s per-key value types are
    // a union of `string` and `number`, so an indexed write would resolve to `never`.
    const patch: Partial<
      Record<keyof PolicyConfig, string | number | string[] | boolean | null>
    > = {};
    for (const group of policyGroups) {
      for (const field of group.fields) {
        // Handled below, outside the generic loop: its dirty/off state lives in
        // `splitFillEnabled`, not in the draft string this loop compares.
        if (field.kind === 'percent-nullable') continue;
        const raw = draft[field.key];
        if (raw === undefined) continue;
        if (toDraftString(current[field.key]) === raw) continue;
        patch[field.key] =
          field.kind === 'banks'
            ? // Comma-separated in the input, an array on the wire. Uppercased because bank
              // codes are symbolic and the matcher compares case-insensitively anyway.
              raw
                .split(',')
                .map((code) => code.trim().toUpperCase())
                .filter(Boolean)
            : field.kind === 'toggle'
              ? raw === 'true'
              : field.kind === 'money'
                ? raw
                : Number(raw);
      }
    }
    if (splitMinSliceEffective !== splitMinSliceCurrent) {
      patch.split_min_slice_percent = splitMinSliceEffective;
    }
    update.mutate(patch as Partial<PolicyConfig>);
  };

  return (
    <div className="space-y-6">
      <Callout tone="info" title={t('settings.policy.openQuestionsTitle')}>
        {t('settings.policy.openQuestionsBody')}
      </Callout>

      {update.isError ? <ErrorState error={update.error} /> : null}
      {update.isSuccess && !dirty ? (
        <Callout tone="success" title={t('settings.policy.savedTitle')}>
          {t('settings.policy.savedBody')}
        </Callout>
      ) : null}

      {policyGroups.map((group) => (
        <Section key={group.title} title={group.title} description={group.description}>
          <Card>
            <CardContent className="grid gap-5 pt-5 sm:grid-cols-2 xl:grid-cols-3">
              {group.fields.map((field) => {
                const value = draft[field.key] ?? '';
                return (
                  <div key={field.key} className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={field.key}>{field.label}</Label>
                      <Pill className="shrink-0">{field.question}</Pill>
                    </div>
                    <div className="flex items-center gap-2">
                      {field.kind === 'toggle' ? (
                        <>
                          <Switch
                            id={field.key}
                            disabled={!can('ADMIN')}
                            checked={value === 'true'}
                            aria-describedby={`${field.key}-help`}
                            onCheckedChange={(checked) => {
                              touched.current.add(field.key);
                              setDraft((prev) => ({ ...prev, [field.key]: String(checked) }));
                            }}
                          />
                          <span className="text-2xs text-muted-foreground">
                            {value === 'true'
                              ? t('settings.policy.toggleOn')
                              : t('settings.policy.toggleOff')}
                          </span>
                        </>
                      ) : field.kind === 'percent-nullable' ? (
                        <>
                          <Switch
                            id={field.key}
                            disabled={!can('ADMIN')}
                            checked={splitFillEnabled}
                            aria-describedby={`${field.key}-help`}
                            onCheckedChange={(checked) => {
                              splitFillTouched.current = true;
                              setSplitFillEnabled(checked);
                              if (checked && value === '') {
                                touched.current.add(field.key);
                                setDraft((prev) => ({ ...prev, [field.key]: '10' }));
                              }
                            }}
                          />
                          <span className="text-2xs shrink-0 text-muted-foreground">
                            {splitFillEnabled
                              ? t('settings.policy.toggleOn')
                              : t('settings.policy.toggleOff')}
                          </span>
                          <Input
                            id={`${field.key}-value`}
                            aria-label={`${field.label} ${t('settings.policy.percentValueSuffix')}`}
                            className="tnum w-20"
                            inputMode="numeric"
                            disabled={!can('ADMIN') || !splitFillEnabled}
                            value={value}
                            aria-describedby={`${field.key}-help`}
                            aria-invalid={splitMinSlicePercentInvalid || undefined}
                            onChange={(event) => {
                              touched.current.add(field.key);
                              setDraft((prev) => ({ ...prev, [field.key]: event.target.value }));
                            }}
                          />
                          <span className="text-2xs shrink-0 text-muted-foreground">%</span>
                        </>
                      ) : (
                        <>
                          <Input
                            id={field.key}
                            className="tnum"
                            inputMode={field.kind === 'banks' ? 'text' : 'decimal'}
                            placeholder={field.kind === 'banks' ? field.placeholder : undefined}
                            disabled={!can('ADMIN')}
                            value={value}
                            aria-describedby={`${field.key}-help`}
                            onChange={(event) => {
                              touched.current.add(field.key);
                              setDraft((prev) => ({ ...prev, [field.key]: event.target.value }));
                            }}
                          />
                          {field.kind === 'seconds' ? (
                            <span className="tnum w-20 shrink-0 text-2xs text-muted-foreground">
                              {formatDuration(seconds(Number(value) || 0))}
                            </span>
                          ) : null}
                        </>
                      )}
                    </div>
                    <p
                      id={`${field.key}-help`}
                      className="text-2xs leading-relaxed text-muted-foreground"
                    >
                      {field.help}
                    </p>
                    {field.kind === 'percent-nullable' && splitMinSlicePercentInvalid ? (
                      <p role="alert" className="text-2xs text-state-bad">
                        {t('settings.policy.percentRangeError')}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </Section>
      ))}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          disabled={!dirty || !can('ADMIN') || splitMinSlicePercentInvalid}
          loading={update.isPending}
          onClick={save}
        >
          <Save aria-hidden="true" />
          {t('settings.policy.saveButton')}
        </Button>
        <Button
          variant="outline"
          disabled={!dirty}
          onClick={() => {
            touched.current.clear();
            splitFillTouched.current = false;
            setDraft(
              Object.fromEntries(
                Object.entries(current).map(([key, value]) => [key, toDraftString(value)]),
              ),
            );
            setSplitFillEnabled(splitMinSliceCurrent !== null);
          }}
        >
          <RotateCcw aria-hidden="true" />
          {t('settings.policy.discardButton')}
        </Button>
        {!can('ADMIN') ? (
          <p className="text-xs text-muted-foreground">{t('settings.policy.readOnlyNote')}</p>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Mock scenario
// ---------------------------------------------------------------------------------------------

function buildScenarios(
  t: (key: string) => string,
): { value: MockScenario; label: string; outcome: string; body: string }[] {
  return [
    {
      value: 'SUCCESSFUL',
      label: t('settings.scenarios.successful.label'),
      outcome: 'AUTO-APPROVE',
      body: t('settings.scenarios.successful.body'),
    },
    {
      value: 'DUPLICATE',
      label: t('settings.scenarios.duplicate.label'),
      outcome: 'REJECT',
      body: t('settings.scenarios.duplicate.body'),
    },
    {
      value: 'NAME_MISMATCH',
      label: t('settings.scenarios.nameMismatch.label'),
      outcome: 'RE-SUBMIT',
      body: t('settings.scenarios.nameMismatch.body'),
    },
    {
      value: 'BANK_MISMATCH',
      label: t('settings.scenarios.bankMismatch.label'),
      outcome: 'RE-SUBMIT',
      body: t('settings.scenarios.bankMismatch.body'),
    },
    {
      value: 'AMOUNT_MISMATCH',
      label: t('settings.scenarios.amountMismatch.label'),
      outcome: 'REVIEW',
      body: t('settings.scenarios.amountMismatch.body'),
    },
    {
      value: 'TIMEOUT',
      label: t('settings.scenarios.timeout.label'),
      outcome: 'RETRY → REVIEW',
      body: t('settings.scenarios.timeout.body'),
    },
    {
      value: 'PROVIDER_FAILURE',
      label: t('settings.scenarios.providerFailure.label'),
      outcome: 'RETRY → REVIEW',
      body: t('settings.scenarios.providerFailure.body'),
    },
    {
      value: 'DELAYED',
      label: t('settings.scenarios.delayed.label'),
      outcome: 'AUTO-APPROVE (slow)',
      body: t('settings.scenarios.delayed.body'),
    },
  ];
}

function ScenarioTab(): ReactNode {
  const { t } = useTranslation();
  const scenario = useMockScenario();
  const setScenario = useSetMockScenario();
  const active = scenario.data?.scenario;
  const scenarios = buildScenarios(t);

  return (
    <Section
      title={t('settings.scenarioTab.sectionTitle')}
      description={t('settings.scenarioTab.sectionDescription')}
    >
      <div className="space-y-4">
        <Callout tone="info" title={t('settings.scenarioTab.devTitle')}>
          {t('settings.scenarioTab.devBody')}
        </Callout>

        {setScenario.isError ? <ErrorState error={setScenario.error} /> : null}

        {scenario.isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-28" />
            ))}
          </div>
        ) : (
          <fieldset>
            <legend className="sr-only">{t('settings.scenarioTab.legend')}</legend>
            <ul className="grid gap-3 sm:grid-cols-2">
              {scenarios.map((item) => {
                const selected = active === item.value;
                return (
                  <li key={item.value}>
                    <label
                      className={cn(
                        'flex h-full cursor-pointer gap-3 rounded-lg border p-4 transition-colors',
                        'focus-within:ring-2 focus-within:ring-ring',
                        selected
                          ? 'border-primary bg-accent'
                          : 'border-border bg-card hover:bg-muted/50',
                      )}
                    >
                      <input
                        type="radio"
                        name="scenario"
                        value={item.value}
                        checked={selected}
                        className="sr-only"
                        onChange={() => {
                          setScenario.mutate(item.value);
                        }}
                      />
                      <span
                        className={cn(
                          'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border',
                          selected ? 'border-primary bg-primary' : 'border-input',
                        )}
                        aria-hidden="true"
                      >
                        {selected ? (
                          <span className="size-1.5 rounded-full bg-primary-foreground" />
                        ) : null}
                      </span>
                      <span className="min-w-0 space-y-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">{item.label}</span>
                          <Pill>{item.outcome}</Pill>
                        </span>
                        <span className="block text-2xs leading-relaxed text-muted-foreground">
                          {item.body}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------------------------
// Profile & 2FA
// ---------------------------------------------------------------------------------------------

function ProfileTab(): ReactNode {
  const { t } = useTranslation();
  const { session } = useAuth();
  if (!session) return null;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Section
        title={t('settings.profile.sectionTitle')}
        description={t('settings.profile.sectionDescription')}
      >
        <Card>
          <CardContent className="pt-5">
            <dl className="grid gap-4 sm:grid-cols-2">
              <Field label={t('settings.profile.fields.name')}>{session.display_name}</Field>
              <Field label={t('settings.profile.fields.email')}>{session.email ?? '—'}</Field>
              <Field
                label={t('settings.profile.fields.role')}
                hint={t('settings.profile.fields.roleHint')}
              >
                <Pill className="bg-accent text-accent-foreground">{session.role}</Pill>
              </Field>
              <Field
                label={t('settings.profile.fields.operatorId')}
                hint={t('settings.profile.fields.operatorIdHint')}
              >
                <Code className="break-all">{session.operator_id ?? '—'}</Code>
              </Field>
              <Field label={t('settings.profile.fields.lastSignIn')}>
                {session.last_sign_in_at ? formatRelative(session.last_sign_in_at) : '—'}
              </Field>
              <Field label={t('settings.profile.fields.participantScope')}>
                <Code>{session.participant}</Code>
              </Field>
            </dl>
          </CardContent>
        </Card>
      </Section>

      <Section
        title={t('settings.profile.twoFactor.sectionTitle')}
        description={t('settings.profile.twoFactor.sectionDescription')}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="size-4 text-muted-foreground" aria-hidden="true" />
              {t('settings.profile.twoFactor.cardTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-4">
              <div className="min-w-0 space-y-0.5">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <BadgeCheck className="size-4 text-state-good" aria-hidden="true" />
                  {t('settings.profile.twoFactor.enabled')}
                </p>
                <p className="text-2xs leading-relaxed text-muted-foreground">
                  {t('settings.profile.twoFactor.requiredNote')}
                </p>
              </div>
              <Switch
                checked
                disabled
                aria-label={t('settings.profile.twoFactor.switchAriaLabel')}
              />
            </div>

            <Button variant="outline" size="sm">
              <FlaskConical aria-hidden="true" />
              {t('settings.profile.twoFactor.regenerateButton')}
            </Button>

            <p className="text-2xs leading-relaxed text-muted-foreground">
              {t('settings.profile.twoFactor.planeNote')}
            </p>
          </CardContent>
        </Card>
      </Section>

      <Section
        title={t('settings.profile.password.sectionTitle')}
        description={t('settings.profile.password.sectionDescription')}
      >
        <ChangePasswordCard />
      </Section>
    </div>
  );
}

/**
 * Self-service password change — every role, own credential only.
 *
 * The current password is re-asked even though the operator is signed in: the cookie proves
 * someone *was* at this keyboard, not that they still are. A wrong current password comes back as
 * a 403 with the session intact, so the form stays up and only says so. On success the server has
 * already revoked every other session this operator held — worth saying out loud, hence the
 * success callout rather than a silent reset.
 */
function ChangePasswordCard(): ReactNode {
  const { t } = useTranslation();
  const change = useChangePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 8;
  const invalid = current.length === 0 || next.length < 8 || next !== confirm;

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (invalid) return;
    change.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: () => {
          setCurrent('');
          setNext('');
          setConfirm('');
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="size-4 text-muted-foreground" aria-hidden="true" />
          {t('settings.profile.password.cardTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} noValidate className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="password-current">{t('settings.profile.password.current')}</Label>
            <Input
              id="password-current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => {
                setCurrent(e.target.value);
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password-new">{t('settings.profile.password.new')}</Label>
            <Input
              id="password-new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => {
                setNext(e.target.value);
              }}
              aria-invalid={tooShort || undefined}
            />
            <p className="text-2xs leading-relaxed text-muted-foreground">
              {t('settings.profile.password.newHint')}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password-confirm">{t('settings.profile.password.confirm')}</Label>
            <Input
              id="password-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
              }}
              aria-invalid={mismatch || undefined}
            />
            {mismatch ? (
              <p role="alert" className="text-2xs text-state-bad">
                {t('settings.profile.password.mismatch')}
              </p>
            ) : null}
          </div>

          {change.isError ? <ErrorState error={change.error} /> : null}
          {change.isSuccess ? (
            <Callout tone="success" title={t('settings.profile.password.changedTitle')}>
              {t('settings.profile.password.changedBody')}
            </Callout>
          ) : null}

          <Button type="submit" disabled={invalid} loading={change.isPending}>
            <Save aria-hidden="true" />
            {t('settings.profile.password.submit')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
